import React, { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { motion, AnimatePresence } from 'framer-motion'

import { AuthProvider } from './components/AuthProvider'
import { ProtectedRoute } from './components/ProtectedRoute'
import { Landing } from './pages/Landing'
import { Login } from './pages/auth/Login'
import { Register } from './pages/auth/Register'
import { Dashboard } from './pages/hr/Dashboard'
import { EmployeeManagement } from './pages/employees/EmployeeManagement'
import { Reports } from './pages/reports/Reports'
import { Settings } from './pages/settings/Settings'
import { AssessmentPortal } from './pages/assessments/AssessmentPortal'
import { EmployeeAssessment } from './pages/assessments/EmployeeAssessment'
import { PredictiveAnalytics } from './pages/analytics/PredictiveAnalytics'
import { EnhancedAnalytics } from './pages/analytics/EnhancedAnalytics'
import { CheckoutSuccess } from './pages/checkout/Success'
import { CheckoutCanceled } from './pages/checkout/Canceled'
import { initSentry } from './lib/sentry'
import './lib/i18n'

// Initialize Sentry
if (import.meta.env.PROD) {
  initSentry()
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <AnimatePresence mode="wait">
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Landing />} />
              <Route path="/auth/login" element={<Login />} />
              <Route path="/auth/register" element={<Register />} />
              
              {/* Employee Assessment Route (Public with token validation) */}
              <Route path="/assessment/:token" element={<EmployeeAssessment />} />
              
              {/* Checkout Routes */}
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
              <Route path="/checkout/canceled" element={<CheckoutCanceled />} />
              
              {/* Protected Routes - HR Only */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute requiredRole="HR">
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employees"
                element={
                  <ProtectedRoute requiredRole="HR">
                    <EmployeeManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute requiredRole="HR">
                    <Reports />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics"
                element={
                  <ProtectedRoute requiredRole="HR">
                    <EnhancedAnalytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute requiredRole="HR">
                    <Settings />
                  </ProtectedRoute>
                }
              />
              
              {/* Phase 2: Employee Portal */}
              <Route
                path="/assessments"
                element={
                  <ProtectedRoute>
                    <AssessmentPortal />
                  </ProtectedRoute>
                }
              />
              
              {/* Error Pages */}
              <Route
                path="/unauthorized"
                element={
                  <div className="min-h-screen flex items-center justify-center bg-calming">
                    <div className="text-center max-w-md mx-auto p-8">
                      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      </div>
                      <h1 className="text-2xl font-bold text-slate-900 mb-2">
                        Access Denied
                      </h1>
                      <p className="text-slate-600 mb-6">
                        You don't have permission to access this page. Please contact your administrator if you believe this is an error.
                      </p>
                      <button
                        onClick={() => window.history.back()}
                        className="bg-sage-600 text-white px-6 py-2 rounded-lg hover:bg-sage-700 transition-colors"
                      >
                        Go Back
                      </button>
                    </div>
                  </div>
                }
              />
              
              {/* 404 Page */}
              <Route
                path="*"
                element={
                  <div className="min-h-screen flex items-center justify-center bg-calming">
                    <div className="text-center max-w-md mx-auto p-8">
                      <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33" />
                        </svg>
                      </div>
                      <h1 className="text-2xl font-bold text-slate-900 mb-2">
                        Page Not Found
                      </h1>
                      <p className="text-slate-600 mb-6">
                        The page you're looking for doesn't exist or has been moved.
                      </p>
                      <a
                        href="/"
                        className="bg-sage-600 text-white px-6 py-2 rounded-lg hover:bg-sage-700 transition-colors inline-block"
                      >
                        Go Home
                      </a>
                    </div>
                  </div>
                }
              />
            </Routes>
          </AnimatePresence>
          
          {/* Toast Notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#fff',
                color: '#374151',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
              },
              success: {
                style: {
                  background: '#f0fdf4',
                  color: '#166534',
                  border: '1px solid #bbf7d0',
                },
              },
              error: {
                style: {
                  background: '#fef2f2',
                  color: '#dc2626',
                  border: '1px solid #fecaca',
                },
              },
            }}
          />
        </div>
      </Router>
    </AuthProvider>
  )
}

export default App